import type { FulfillmentMethod } from '../types/store-fulfillment';
import { getOrderStatusTranslationKey } from '../lib/store-fulfillment';
/**
 * F-018: Reusable order status timeline.
 *
 * Desktop (md:): horizontal stepper with 3 forward states + terminal
 * variant, connected by lines. Mobile: vertical list.
 *
 * Steps: Ordered -> Shipped -> Delivered
 * Terminal variant: shows the path plus a Cancelled or Rejected terminus.
 *
 * RTL: flex + dir attribute handles reading direction. Connecting lines
 * use logical start-0/end-0. Step order reads naturally in both LTR/RTL.
 *
 * Dates formatted via Intl.DateTimeFormat with active locale.
 */
import { useTranslation } from '../i18n';
import type { OrderStatus } from '../types/order';

export interface OrderStatusTimelineProps {
  readonly status: OrderStatus;
  readonly method?: FulfillmentMethod;
  readonly shippedAt: string | null;
  readonly deliveredAt: string | null;
  readonly cancelledAt: string | null;
  readonly rejectedAt?: string | null;
  readonly variant?: 'legacy' | 'storefrontNeutral';
}

interface TimelineStep {
  readonly key: 'ordered' | 'shipped' | 'delivered';
  readonly dateField: string | null;
}

type DesktopTimelineStep =
  | TimelineStep
  | { readonly key: 'cancelled' | 'rejected'; readonly dateField: string | null };

/** Map of forward statuses to their sequential index. */
const FORWARD_STATUS_ORDER: Record<string, number> = {
  ordered: 0,
  shipped: 1,
  delivered: 2,
};

function formatStepDate(isoDate: string | null, locale: string): string | null {
  if (!isoDate) return null;
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(isoDate));
  } catch {
    return isoDate;
  }
}

export function OrderStatusTimeline({
  status,
  shippedAt,
  deliveredAt,
  cancelledAt,
  rejectedAt,
  variant = 'legacy',
  method = 'delivery',
}: OrderStatusTimelineProps): React.JSX.Element {
  const { t, lang } = useTranslation();
  const isStorefrontNeutral = variant === 'storefrontNeutral';

  const isTerminalExit = status === 'cancelled' || status === 'rejected';
  const forwardOrder = method === 'pickup' ? { ordered: 0, delivered: 1 } : FORWARD_STATUS_ORDER;
  const currentIndex = forwardOrder[status as keyof typeof forwardOrder] ?? -1;

  const steps: TimelineStep[] = [
    { key: 'ordered', dateField: null },
    ...(method === 'pickup' ? [] : [{ key: 'shipped' as const, dateField: shippedAt }]),
    { key: 'delivered', dateField: deliveredAt },
  ];
  const terminalStep =
    status === 'rejected'
      ? ({ key: 'rejected', dateField: rejectedAt ?? null } as const)
      : ({ key: 'cancelled', dateField: cancelledAt } as const);
  const desktopSteps: DesktopTimelineStep[] = isTerminalExit ? [...steps, terminalStep] : steps;
  const desktopGridClass =
    desktopSteps.length === 2
      ? 'grid-cols-2'
      : desktopSteps.length === 3
        ? 'grid-cols-3'
        : 'grid-cols-4';
  const desktopRailInsetClass =
    desktopSteps.length === 2
      ? 'start-[25%] end-[25%]'
      : desktopSteps.length === 4
        ? 'start-[12.5%] end-[12.5%]'
        : 'start-[16.666%] end-[16.666%]';
  const desktopRailSegments = desktopSteps.length - 1;
  const completedClass = isStorefrontNeutral ? 'bg-teal-600' : 'bg-[#7A8B4A]';
  const currentClass = isStorefrontNeutral
    ? 'bg-teal-600 ring-4 ring-teal-600/20'
    : 'bg-[#7A8B4A] ring-4 ring-[#7A8B4A]/20';
  const terminalClass = isStorefrontNeutral ? 'bg-red-600' : 'bg-[#B8472F]';
  const terminalTextClass = isStorefrontNeutral
    ? 'text-red-700 dark:text-red-300'
    : 'text-[#B8472F]';
  const inactiveDotClass = isStorefrontNeutral
    ? 'bg-gray-300 dark:bg-gray-700'
    : 'bg-stone-300 dark:bg-stone-600';
  const inactiveRailClass = isStorefrontNeutral
    ? 'bg-gray-200 dark:bg-gray-800'
    : 'bg-stone-200 dark:bg-stone-700';
  const primaryTextClass = isStorefrontNeutral
    ? 'text-gray-950 dark:text-gray-100'
    : 'text-stone-900 dark:text-stone-100';
  const mutedTextClass = isStorefrontNeutral
    ? 'text-gray-400 dark:text-gray-500'
    : 'text-stone-400 dark:text-stone-500';
  const timestampTextClass = isStorefrontNeutral
    ? 'text-gray-500 dark:text-gray-400'
    : 'text-stone-500 dark:text-stone-400';

  return (
    <div data-testid="order-status-timeline" className="w-full">
      <h3 className={`mb-4 text-base font-semibold ${primaryTextClass}`}>
        {t('order.timeline.title')}
      </h3>

      {/* Desktop: horizontal stepper */}
      <div className="hidden md:block">
        <div className="relative h-5" data-testid="order-status-timeline-desktop-rail-row">
          <div
            data-testid="order-status-timeline-desktop-rail"
            className={`absolute top-1/2 flex h-0.5 -translate-y-1/2 ${desktopRailInsetClass}`}
          >
            {Array.from({ length: desktopRailSegments }).map((_, segmentIndex) => {
              const segmentClass = isTerminalExit
                ? segmentIndex === desktopRailSegments - 1
                  ? terminalClass
                  : inactiveRailClass
                : segmentIndex < currentIndex
                  ? completedClass
                  : inactiveRailClass;

              return (
                <div
                  // Segment index is stable because the timeline status order is fixed.
                  key={segmentIndex}
                  data-testid="order-status-timeline-desktop-rail-segment"
                  className={`h-0.5 flex-1 ${segmentClass}`}
                />
              );
            })}
          </div>
          <div className={`relative z-10 grid h-5 ${desktopGridClass}`}>
            {desktopSteps.map((step) => {
              if (step.key === 'cancelled' || step.key === 'rejected') {
                return (
                  <div key={step.key} className="flex justify-center">
                    <div
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white ${terminalClass}`}
                    >
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                  </div>
                );
              }

              const stepIndex = forwardOrder[step.key as keyof typeof forwardOrder] ?? 0;
              const isOrderedTerminalStep = isTerminalExit && step.key === 'ordered';
              const isCompleted =
                (!isTerminalExit && stepIndex <= currentIndex) || isOrderedTerminalStep;
              const isCurrent = !isTerminalExit && stepIndex === currentIndex;

              return (
                <div key={step.key} className="flex justify-center">
                  <div
                    className={`mt-1 h-3 w-3 shrink-0 rounded-full ${
                      isCurrent ? currentClass : isCompleted ? completedClass : inactiveDotClass
                    }`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className={`mt-1 grid ${desktopGridClass}`}>
          {desktopSteps.map((step) => {
            const isTerminalStep = step.key === 'cancelled' || step.key === 'rejected';
            const stepIndex = isTerminalStep
              ? -1
              : (forwardOrder[step.key as keyof typeof forwardOrder] ?? 0);
            const isOrderedTerminalStep = isTerminalExit && step.key === 'ordered';
            const isCompleted =
              (!isTerminalExit && stepIndex <= currentIndex) || isOrderedTerminalStep;
            const dateStr = formatStepDate(step.dateField, lang);

            return (
              <div key={step.key} className="flex flex-col items-center gap-1 text-center">
                <span
                  className={`text-xs font-medium ${
                    isTerminalStep
                      ? terminalTextClass
                      : isCompleted
                        ? primaryTextClass
                        : mutedTextClass
                  }`}
                >
                  {t(getOrderStatusTranslationKey(step.key, method))}
                </span>
                {dateStr && (
                  <span className={`text-[10px] ${timestampTextClass}`}>
                    {t('order.timeline.completedOn', { date: dateStr })}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile: vertical list */}
      <div className="flex flex-col gap-3 md:hidden">
        {steps.map((step, idx) => {
          const stepIndex = forwardOrder[step.key as keyof typeof forwardOrder] ?? 0;
          const isOrderedTerminalStep = isTerminalExit && step.key === 'ordered';
          const isCompleted =
            (!isTerminalExit && stepIndex <= currentIndex) || isOrderedTerminalStep;
          const isCurrent = !isTerminalExit && stepIndex === currentIndex;
          const dateStr = formatStepDate(step.dateField, lang);

          return (
            <div key={step.key} className="flex items-start gap-3">
              {/* Dot + connector column */}
              <div className="flex flex-col items-center">
                <div
                  className={`h-3 w-3 shrink-0 rounded-full ${
                    isCurrent ? currentClass : isCompleted ? completedClass : inactiveDotClass
                  }`}
                />
                {idx < steps.length - 1 && (
                  <div
                    className={`w-0.5 flex-1 ${
                      !isTerminalExit && stepIndex < currentIndex
                        ? completedClass
                        : inactiveRailClass
                    }`}
                    style={{ minHeight: '24px' }}
                  />
                )}
              </div>
              {/* Label + date */}
              <div className="flex flex-col">
                <span
                  className={`text-sm font-medium ${
                    isCompleted ? primaryTextClass : mutedTextClass
                  }`}
                >
                  {t(getOrderStatusTranslationKey(step.key, method))}
                </span>
                {dateStr && (
                  <span className={`text-xs ${timestampTextClass}`}>
                    {t('order.timeline.completedOn', { date: dateStr })}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {/* Terminal terminus (mobile) */}
        {isTerminalExit && (
          <div className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <div
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white ${terminalClass}`}
              >
                <svg
                  className="h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <div className="flex flex-col">
              <span className={`text-sm font-medium ${terminalTextClass}`}>
                {t(`order.status.${terminalStep.key}`)}
              </span>
              {terminalStep.dateField && (
                <span className={`text-xs ${timestampTextClass}`}>
                  {t('order.timeline.completedOn', {
                    date: formatStepDate(terminalStep.dateField, lang) ?? '',
                  })}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
